import {getStorageType} from './storage'

type ImageFile = {
  url?: string | null
  image_url?: string | null
  drive_file_id?: string | null
  is_gdrive_platform?: boolean | null
}

export const detectHeicMimeType = async (blob: Blob): Promise<string | null> => {
  const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read image header'))
    reader.readAsArrayBuffer(blob.slice(0, 12))
  })
  const header = new Uint8Array(buffer)
  if (header.length < 12) return null
  const fourCC = (offset: number) =>
    String.fromCharCode(...Array.from(header.subarray(offset, offset + 4)))
  if (fourCC(4) !== 'ftyp') return null

  const brand = fourCC(8)
  if (['heic', 'heix', 'hevc', 'hevx'].includes(brand)) return 'image/heic'
  if (['mif1', 'msf1'].includes(brand)) return 'image/heif'
  return null
}

export const resolveImageUrl = (file?: ImageFile | null): string => {
  if (!file) return ''

  const url = file.url?.trim() || file.image_url?.trim() || ''
  const drivePath = url.match(/(?:^|\/)patient\/drive\/image\/([^/?#]+)/)
  const driveId = file.drive_file_id || drivePath?.[1]

  if ((file.is_gdrive_platform || drivePath) && driveId) {
    const baseUrl = (process.env.REACT_APP_BASE_APP_PATIENT_URL || '').replace(/\/+$/, '')
    const query = drivePath ? (url.match(/\?[^#]*/)?.[0] ?? '') : ''
    return `${baseUrl}/patient/drive/image/${encodeURIComponent(driveId)}${query}`
  }

  return url
}

export const isPatientDriveImageUrl = (src: string): boolean => {
  try {
    const baseUrl = new URL(
      process.env.REACT_APP_BASE_APP_PATIENT_URL || window.location.origin,
      window.location.origin
    )
    const imageUrl = new URL(src, window.location.origin)
    const imagePath = `${baseUrl.pathname.replace(/\/+$/, '')}/patient/drive/image/`
    return imageUrl.origin === baseUrl.origin && imageUrl.pathname.startsWith(imagePath)
  } catch {
    return false
  }
}

export const getPatientImageHeaders = (): HeadersInit | undefined => {
  const storage = getStorageType()
  const token =
    storage.getItem('userToken') ||
    storage.getItem('ds_token') ||
    window.sessionStorage.getItem('ds_token')
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = /^bearer\s/i.test(token) ? token : `Bearer ${token}`
  for (const key of ['organizationId', 'profileId', 'userId']) {
    const value = storage.getItem(key)
    if (!value) continue
    const header =
      key === 'userId' ? 'User-Id' : key === 'profileId' ? 'profile_id' : 'organization_id'
    headers[header] = value
  }
  if (headers['User-Id']) headers['User-Type'] = 'DOCTOR'

  return Object.keys(headers).length > 0 ? headers : undefined
}
